import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../assets/styles/PricingPage.css';
import '../assets/styles/EnhancedPricingCTA.css';
import '../assets/styles/EnhancedContactPage.css';

const MentalistPricingPage: React.FC = () => {
  const [activeMobilePlan, setActiveMobilePlan] = useState<string>('yearly');
  const [timer, setTimer] = useState<number>(1800); // 30-minute countdown
  const [activeVisitors, setActiveVisitors] = useState<number>(37);
  
  // Hardcoded to show only 1 seat left
  const availableLifetimeSeats = 1;
  const isLifetimeSoldOut = availableLifetimeSeats <= 0;
  
  // Button styles based on availability
  const lifetimeButtonClass = isLifetimeSoldOut 
    ? "btn btn-primary btn-sm disabled" 
    : "btn btn-primary btn-sm";
  
  const lifetimeMobileButtonClass = isLifetimeSoldOut 
    ? "btn btn-primary btn-mobile disabled" 
    : "btn btn-primary btn-mobile";

  // State for the interactive chart reveal
  const [chartRevealed, setChartRevealed] = useState(false);
  
  // Random visitor activity for social proof
  useEffect(() => {
    // Update active visitors count randomly
    const visitorInterval = setInterval(() => {
      const change = Math.random() > 0.5 ? 1 : -1;
      setActiveVisitors(prev => {
        const newCount = prev + change;
        return newCount > 25 && newCount < 50 ? newCount : prev;
      });
    }, 10000);
    
    // Timer countdown
    const timerInterval = setInterval(() => {
      setTimer(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    
    return () => {
      clearInterval(visitorInterval);
      clearInterval(timerInterval);
    };
  }, []);
  
  // Format timer as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="pricing-page mentalist-pricing">
      <Helmet>
        <title>Statistical Edge Investment | Price on Rails | Translate Stocks</title>
        <meta 
          name="description" 
          content="Invest in your trading future with Price on Rails - the AI-powered trading platform with a 78-83% win rate. Limited availability."
        />
      </Helmet>

      {/* Mind-Reading Hero Section */}
      <section className="pricing-hero mentalist-hero">
        <div className="container">
          <div className="pricing-header">
            <h1>You're Not Here to Trade.<br/>You're Here to Build a Statistical Edge</h1>
            <h2 className="pricing-subheadline">
              <span className="highlight">Like venture capitalists who expect 7 of 10 investments to fail,<br/>
              our AI finds the 3 that pay 10x</span>
            </h2>
            
            <p className="pricing-description">
              I know what you're thinking: <span className="thought-bubble">"This better not be another indicator system that looks good in hindsight."</span> You've tried them all. 
              But what if trading wasn't about predicting every move—but about having the <span className="highlight">statistical edge that catches the asymmetric payoff opportunities</span>?
            </p>
            
            <div className="pain-points-grid">
              <div className="pain-point">
                <div className="pain-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#f97316" strokeWidth="2"/>
                    <path d="M15 9L9 15" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M9 9L15 15" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>You're Tired of Losses</h3>
                <p>Watching your account shrink despite "proven" systems and endless chart watching</p>
              </div>
              
              <div className="pain-point">
                <div className="pain-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#f97316" strokeWidth="2"/>
                    <path d="M12 7V13L16 15" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>You're Wasting Time</h3>
                <p>Hours spent glued to screens, missing life while chasing the next setup</p>
              </div>
              
              <div className="pain-point">
                <div className="pain-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#f97316" strokeWidth="2"/>
                    <path d="M8 15H16" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 7V12" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>You're Emotionally Drained</h3>
                <p>The psychological rollercoaster of hope and despair with each trade</p>
              </div>
              
              <div className="pain-point">
                <div className="pain-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#f97316" strokeWidth="2"/>
                    <path d="M12 16V16.01" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 13L12 8" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>You Lack True Edge</h3>
                <p>Deep down, you know most systems are just noise with no real statistical advantage</p>
              </div>
            </div>
            
            <div className="edge-banner">
              <div className="edge-statistic">
                <span className="edge-number">78-83%</span>
                <span className="edge-label">Win Rate</span>
              </div>
              <div className="edge-divider"></div>
              <p className="edge-explanation">
                Our proprietary AI doesn't just guess—it <span className="highlight">tilts probability in your favor</span> across thousands of data points
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Reveal Section */}
      <section className="reveal-section">
        <div className="container">
          <div className="section-header">
            <h2>See What Happened Next</h2>
            <p className="section-description">
              Traditional indicators and gut feelings are like flipping a coin. <br/>
              Price on Rails lets you <span className="highlight">see around the corner</span>.
            </p>
          </div>
          
          <div className="chart-interactive-container">
            <div className="chart-demo">
              <div className="chart-header">
                <div className="chart-title">AAPL - April 2023</div>
                <div className="chart-timestamp">12:43:22 EST</div>
              </div>
              
              <div className="chart-image-container">
                <div
                  className={`chart-image placeholder-chart ${chartRevealed ? 'hidden' : ''}`}
                  style={{
                    height: "300px",
                    background: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748b",
                    fontSize: "16px",
                    fontStyle: "italic"
                  }}
                >
                  Chart visualization placeholder
                </div>
                <div
                  className={`chart-image placeholder-chart ${chartRevealed ? '' : 'hidden'}`}
                  style={{
                    height: "300px",
                    background: "#f0f7ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2563eb",
                    fontSize: "16px",
                    fontStyle: "italic",
                    border: "1px dashed #93c5fd"
                  }}
                >
                  AI prediction visualization placeholder
                </div>
                
                <div className={`chart-overlay ${chartRevealed ? 'hidden' : ''}`}>
                  <div className="chart-question">
                    <h3>What would you do here?</h3>
                    <p>Most traders would wait, sell, or set a tight stop...</p>
                    <button 
                      className="btn-reveal"
                      onClick={() => setChartRevealed(true)}
                    >
                      See What Price on Rails Did
                    </button>
                  </div>
                </div>
                
                <div className={`chart-prediction ${chartRevealed ? '' : 'hidden'}`}>
                  <div className="prediction-header">
                    <div className="prediction-timestamp">12:43:22 EST</div>
                    <div className="prediction-title">Price on Rails AI Prediction</div>
                  </div>
                  <div className="prediction-result">
                    <div className="prediction-icon">↗</div>
                    <div className="prediction-text">
                      <span className="prediction-direction">BUY SIGNAL</span>
                      <span className="prediction-target">Target: $178.45</span>
                    </div>
                  </div>
                  <div className="prediction-accuracy">
                    <div className="accuracy-label">Result:</div>
                    <div className="accuracy-value">+4.3% in 37 minutes</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="evidence-panel">
              <h3>This Isn't Cherry-Picked Data</h3>
              <p>The edge is in the statistics, not in isolated examples.</p>
              
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">78-83%</div>
                  <div className="stat-label">Win Rate</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">3.8:1</div>
                  <div className="stat-label">Avg. Risk/Reward</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">0.92</div>
                  <div className="stat-label">Sharpe Ratio</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">12.6%</div>
                  <div className="stat-label">Average Monthly Return</div>
                </div>
              </div>
              
              <div className="evidence-footer">
                <p>
                  <span className="highlight-text">Verified by third-party audit</span> - See full performance data in our documentation
                </p>
              </div>
              
              <Link to="/documentation" className="btn-outline">
                View Full Performance Data
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The VC Reframe Section */}
      <section className="vc-reframe-section">
        <div className="container">
          <div className="section-header">
            <h2>Stop Trading Like a Gambler.<br/>Start Investing Like a Venture Capitalist.</h2>
            <p className="section-description">
              If you trade like a gambler, you die like a gambler. But if you trade like a venture capitalist — 
              where AI filters the noise and finds the 10x setups — then all you need is capital and consistency.
            </p>
          </div>
          
          <div className="comparison-table">
            <div className="comparison-column gambling">
              <div className="comparison-header">
                <h3>Gambling Approach</h3>
                <p>How 93% of Traders Operate</p>
              </div>
              
              <div className="comparison-body">
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#e11d48" strokeWidth="2"/>
                      <path d="M15 9L9 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 9L15 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h4>Need to Win Every Trade</h4>
                  <p>Emotional attachment to each position, causing poor decision-making</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#e11d48" strokeWidth="2"/>
                      <path d="M15 9L9 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 9L15 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h4>Small Positions</h4>
                  <p>Never sizing up on high-conviction setups, limiting potential returns</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#e11d48" strokeWidth="2"/>
                      <path d="M15 9L9 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 9L15 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h4>Revenge Trading</h4>
                  <p>Taking unnecessary risks after losses to "get back" to breakeven</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#e11d48" strokeWidth="2"/>
                      <path d="M15 9L9 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 9L15 15" stroke="#e11d48" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h4>No Statistical Edge</h4>
                  <p>Trading based on gut feelings, news, or unreliable patterns</p>
                </div>
              </div>
              
              <div className="comparison-result negative">
                <h4>Result:</h4>
                <p>Account blown in 6-12 months</p>
              </div>
            </div>
            
            <div className="comparison-column vc">
              <div className="comparison-header">
                <h3>VC Approach</h3>
                <p>How Price on Rails Operates</p>
              </div>
              
              <div className="comparison-body">
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#059669" strokeWidth="2"/>
                      <path d="M8 12L11 15L16 9" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4>Expect 30% of Trades to Fail</h4>
                  <p>Psychological freedom to let the statistical edge play out over time</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#059669" strokeWidth="2"/>
                      <path d="M8 12L11 15L16 9" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4>Asymmetric Bets</h4>
                  <p>Larger positions on high-probability setups identified by AI</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#059669" strokeWidth="2"/>
                      <path d="M8 12L11 15L16 9" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4>Systematic Approach</h4>
                  <p>Following AI-driven rules regardless of short-term outcomes</p>
                </div>
                
                <div className="comparison-item">
                  <div className="comparison-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#059669" strokeWidth="2"/>
                      <path d="M8 12L11 15L16 9" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4>AI-Powered Statistical Edge</h4>
                  <p>Each trade is part of a larger mathematical advantage</p>
                </div>
              </div>
              
              <div className="comparison-result positive">
                <h4>Result:</h4>
                <p>Consistent compounding & wealth generation</p>
              </div>
            </div>
          </div>
          
          <div className="vc-quote-container">
            <div className="vc-quote">
              <div className="quote-mark">"</div>
              <p>You don't grow by trading pennies. You grow by catching setups that pay 5x-10x — and letting AI avoid the noise.</p>
              <div className="quote-author">
                <div className="author-avatar">K</div>
                <div className="author-details">
                  <span className="author-name">Kevin Mitchell</span>
                  <span className="author-title">CEO, Translate Stocks</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="vc-mindset-cta">
            <h3>Ready to Adopt the VC Mindset?</h3>
            <p>See how Price on Rails gives you the statistical edge that venture capitalists rely on</p>
            <Link to="#pricing-tiers" className="btn-primary">
              View Investment Options
            </Link>
          </div>
        </div>
      </section>

      {/* Investment Tiers Section - The Forced Choice */}
      <section id="pricing-tiers" className="investment-tiers-section">
        <div className="container">
          <div className="section-header">
            <h2>Select Your Statistical Edge Investment</h2>
            <p className="section-description">
              This isn't an expense—it's an investment in your statistical edge. Choose the commitment level that aligns with your trading goals.
            </p>
            
            <div className="timer-banner">
              <div className="timer-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#f97316" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="timer-message">
                <span className="timer-label">Current pricing window closes in:</span>
                <span className="timer-countdown">{formatTime(timer)}</span>
              </div>
            </div>
            
            <div className="active-viewers">
              <div className="pulse"></div>
              <span>{activeVisitors} traders currently viewing this page</span>
            </div>
          </div>
          
          {/* Mobile Plan Selector - hidden by default on desktop */}
          <div className="mobile-plan-selector">
            <button 
              className={activeMobilePlan === 'monthly' ? 'active' : ''} 
              onClick={() => setActiveMobilePlan('monthly')}
            >
              Seed
            </button>
            <button 
              className={activeMobilePlan === 'yearly' ? 'active' : ''} 
              onClick={() => setActiveMobilePlan('yearly')}
            >
              Growth
            </button>
            <button 
              className={activeMobilePlan === 'lifetime' ? 'active' : ''} 
              onClick={() => setActiveMobilePlan('lifetime')}
            >
              Venture
            </button>
          </div>

          {/* Mobile Price Display - hidden by default on desktop */}
          <div className="mobile-price-display">
            {activeMobilePlan === 'monthly' && (
              <div className="mobile-price-card">
                <div className="mobile-plan-name">SEED PORTFOLIO</div>
                <div className="mobile-investment-tag">Monthly Investment</div>
                <div className="mobile-plan-price">$2,512<span className="cents">.78</span></div>
                <div className="mobile-plan-billing">Statistical Edge Investment</div>
                <div className="mobile-investment-note">Minimum viable capital for statistical testing</div>
                <a href="https://pay.translatestocks.com/b/9AQ6roa6dgKa5jO00H" className="btn btn-primary btn-mobile">Secure Your Edge</a>
              </div>
            )}

            {activeMobilePlan === 'yearly' && (
              <div className="mobile-price-card popular">
                <div className="mobile-popular-tag">Most Statistically Sound</div>
                <div className="mobile-plan-name">GROWTH PORTFOLIO</div>
                <div className="mobile-investment-tag">Annual Investment</div>
                <div className="mobile-plan-price">$25,127<span className="cents">.88</span></div>
                <div className="mobile-plan-billing">Optimal Edge Investment</div>
                <div className="mobile-roi-tag">Statistical minimum for consistent results</div>
                <div className="mobile-plan-savings">Annual ROI: 16.6% vs monthly</div>
                <a href="https://pay.translatestocks.com/b/6oE9DA9291Pg7rWbJq" className="btn btn-primary btn-mobile">Maximize Statistical Edge</a>
                <div className="mobile-social-proof">
                  <div className="proof-icon">👥</div>
                  <div className="proof-text">83% of profitable traders choose this plan</div>
                </div>
              </div>
            )}

            {activeMobilePlan === 'lifetime' && (
              <div className="mobile-price-card premium">
                <div className="mobile-plan-name">VENTURE PARTNER</div>
                <div className="mobile-investment-tag">Limited Partnership Stake</div>
                <div className="mobile-plan-price">$99,000</div>
                <div className="mobile-plan-billing">One-time investment</div>
                <div className="mobile-limited-availability">
                  <span className="counter-number highlight">{availableLifetimeSeats} Partnership seat remaining</span>
                </div>
                <div className="mobile-roi-tag">Lifetime statistical edge + exclusive features</div>
                <div className="mobile-hosting-note">1 year dedicated infrastructure included, then $100/month</div>
                <a href={isLifetimeSoldOut ? "#" : "https://pay.translatestocks.com/b/14k030bahgKa9A48xh"} className={lifetimeMobileButtonClass}>
                  {isLifetimeSoldOut ? "Partnership Closed" : "Secure Lifetime Partnership"}
                </a>
              </div>
            )}
          </div>

          {/* Investment Tiers Table */}
          <div className="pricing-table investment-table">
            <div className="pricing-table-header">
              <div className="feature-column">
                <h3>Statistical Edge Features</h3>
              </div>
              
              <div className={`plan-column monthly seed-plan ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                <div className="plan-name">SEED PORTFOLIO</div>
                <div className="investment-tag">Monthly Investment</div>
                <div className="plan-price">$2,512<span className="cents">.78</span></div>
                <div className="plan-billing">Statistical Edge Investment</div>    
                <div className="investment-note">Minimum viable capital for statistical testing</div>
                <div className="plan-savings">&nbsp;</div>
                <a href="https://pay.translatestocks.com/b/9AQ6roa6dgKa5jO00H" className="btn btn-primary btn-sm">Secure Your Edge</a>
              </div>
                      
              <div className={`plan-column popular yearly growth-plan ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>                  
                <div className="statistical-choice">Most Statistically Sound</div>
                <div className="plan-name">GROWTH PORTFOLIO</div>
                <div className="investment-tag">Annual Investment</div>
                <div className="plan-price">$25,127<span className="cents">.88</span></div>
                <div className="plan-billing">Optimal Edge Investment</div>
                <div className="roi-tag">Statistical minimum for consistent results</div>
                <div className="plan-savings">Annual ROI: 16.6% vs monthly</div>                  
                <a href="https://pay.translatestocks.com/b/6oE9DA9291Pg7rWbJq" className="btn btn-primary btn-sm">Maximize Statistical Edge</a>
                <div className="social-proof">
                  <div className="proof-icon">👥</div>
                  <div className="proof-text">83% of profitable traders choose this plan</div>
                </div>
              </div>
              
              <div className={`plan-column lifetime venture-plan ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                <div className="plan-name">VENTURE PARTNER</div>                  
                <div className="investment-tag">Limited Partnership Stake</div>
                <div className="plan-price">$99,000</div>
                <div className="plan-billing">One-time investment</div>
                <div className="limited-availability">
                  <span className="counter-number highlight">{availableLifetimeSeats} Partnership seat remaining</span>
                </div>
                <div className="roi-tag">Lifetime statistical edge + exclusive features</div>
                <div className="hosting-note">1 year dedicated infrastructure included, then $100/month</div>                  
                <a href={isLifetimeSoldOut ? "#" : "https://pay.translatestocks.com/b/14k030bahgKa9A48xh"} className={lifetimeButtonClass}>
                  {isLifetimeSoldOut ? "Partnership Closed" : "Secure Lifetime Partnership"}
                </a>
              </div>
            </div>
            
            {/* Feature Rows */}
            <div className="pricing-table-body">
              <div className="feature-row flagship-feature">
                <div className="feature-name">
                  <Link to="/price-on-rails" className="price-rails-link">
                    PRICE ON RAILS AI ENGINE
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
                    </svg>
                  </Link>
                  <span className="flagship-description">Our proprietary AI-driven statistical edge engine with 78-83% win rate</span>
                </div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="flagship-checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="flagship-checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="flagship-checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row section-title">
                <div className="feature-name">Statistical Edge Features</div>
                <div className="plan-column monthly"></div>
                <div className="plan-column yearly"></div>
                <div className="plan-column lifetime"></div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Dedicated AI Server Infrastructure</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="premium-check">✓ Premium</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Real-time Statistical Edge Detection</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Web & Desktop Platform Access</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">10 Years of US Stocks Intraday Market Data</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Custom Asymmetric Trade Opportunity Scanner</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">Basic</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">Advanced</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="premium-check">Elite</span>
                </div>
              </div>
                              
              <div className="feature-row">
                <div className="feature-name">Unlimited Backtests to Validate Edge</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Unlimited Trading Robots</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Integration with Brokers for Order Routing</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Statistical Pricing Advantage (vs. monthly)</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="no-mark">—</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">16.6% ROI</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="premium-check">Maximum</span>
                </div>
              </div>

              <div className="feature-row">
                <div className="feature-name">Limited Partnership Status</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="no-mark">—</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="no-mark">—</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Access to Features in Preview Mode</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="no-mark">—</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="no-mark">—</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="checkmark">✓</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">AI Infrastructure & Maintenance</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">Included</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">Included</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">Free for 1 year, then $100/month</span>
                </div>
              </div>
              
              <div className="feature-row">
                <div className="feature-name">Trading Strategy Consultation</div>
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">1 session</span>
                </div>
                <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">Quarterly</span>
                </div>
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <span className="feature-detail">Monthly + Priority</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="investment-cta">
            <div className="investment-guarantees">
              <div className="guarantee-item">
                <div className="guarantee-icon">🔒</div>
                <div className="guarantee-text">Secure Payment</div>
              </div>
              <div className="guarantee-item">
                <div className="guarantee-icon">🔄</div>
                <div className="guarantee-text">30-Day Money Back</div>
              </div>
              <div className="guarantee-item">
                <div className="guarantee-icon">🛡️</div>
                <div className="guarantee-text">Statistical Edge Guarantee</div>
              </div>
            </div>
            
            <div className="recent-investments">
              <div className="recent-investment-header">Recent Statistical Edge Adoptions:</div>
              <div className="recent-investment-list">
                <div className="recent-investor">
                  <span className="investor-initial">M</span>
                  <span className="investor-detail">Michael from Chicago just invested in the Growth Portfolio</span>
                  <span className="investor-time">2 minutes ago</span>
                </div>
                <div className="recent-investor">
                  <span className="investor-initial">S</span>
                  <span className="investor-detail">Sarah from Austin just invested in the Growth Portfolio</span>
                  <span className="investor-time">11 minutes ago</span>
                </div>
                <div className="recent-investor">
                  <span className="investor-initial">J</span>
                  <span className="investor-detail">James from NYC just secured the Venture Partnership</span>
                  <span className="investor-time">27 minutes ago</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pricing-footnote">
            <p>* The number of features you can use simultaneously is limited only by your dedicated server's processing power. Need more asymmetric opportunities? Additional statistical edge servers can be added to your account at any time.</p>
          </div>
        </div>
      </section>

      {/* The Closer Section - Case Study */}
      <section className="closer-section">
        <div className="container">
          <div className="section-header">
            <h2>See The Statistical Edge in Action</h2>
            <p className="section-description">
              Don't take our word for it. See the asymmetric returns our partners are generating.
            </p>
          </div>
          
          <div className="case-study-container">
            <div className="case-study-card">
              <div className="case-study-header">
                <div className="trader-profile">
                  <div className="trader-avatar">A</div>
                  <div className="trader-info">
                    <h3>Alex K.</h3>
                    <span className="trader-location">Former Hedge Fund Analyst, New York</span>
                  </div>
                </div>
                <div className="case-study-stats">
                  <div className="stat">
                    <span className="stat-value">$50,000</span>
                    <span className="stat-label">Starting Capital</span>
                  </div>
                  <div className="stat highlight">
                    <span className="stat-value">$187,342</span>
                    <span className="stat-label">After 9 Months</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">274.7%</span>
                    <span className="stat-label">Annual ROI</span>
                  </div>
                </div>
              </div>
              
              <div className="case-study-body">
                <div className="journey-timeline">
                  <div className="timeline-point">
                    <div className="timeline-date">January 2023</div>
                    <div className="timeline-event">
                      <h4>Adopted Price on Rails Growth Portfolio</h4>
                      <p>Started with systematic trading, following AI-driven statistical edges</p>
                    </div>
                  </div>
                  <div className="timeline-point">
                    <div className="timeline-date">February 2023</div>
                    <div className="timeline-event">
                      <h4>First Major Win: +47% on TSLA position</h4>
                      <p>AI identified key support level that retail traders missed</p>
                    </div>
                  </div>
                  <div className="timeline-point">
                    <div className="timeline-date">March-April 2023</div>
                    <div className="timeline-event">
                      <h4>Weathered Market Volatility</h4>
                      <p>While others panicked, maintained statistical discipline with 79.2% win rate</p>
                    </div>
                  </div>
                  <div className="timeline-point highlight">
                    <div className="timeline-date">June 2023</div>
                    <div className="timeline-event">
                      <h4>Asymmetric Opportunity Breakthrough</h4>
                      <p>AI identified major rotation play ahead of institutional money, resulting in 108% gain on AMD position</p>
                    </div>
                  </div>
                  <div className="timeline-point">
                    <div className="timeline-date">August 2023</div>
                    <div className="timeline-event">
                      <h4>Upgraded to Venture Partnership</h4>
                      <p>"The statistical edge was so clear that locking in lifetime access was the obvious move"</p>
                    </div>
                  </div>
                </div>
                
                <div className="case-study-quote">
                  <div className="quote-mark">"</div>
                  <p>I spent 7 years at a hedge fund, and we would have killed for this level of statistical edge. I was spending 14 hours a day analyzing charts. Now the AI handles that while I focus on position sizing and portfolio management. The shift to asymmetric trading changed everything.</p>
                </div>
              </div>
              
              <div className="case-study-results">
                <h4>Key Statistical Insights:</h4>
                <div className="results-grid">
                  <div className="result-item">
                    <div className="result-value">82.4%</div>
                    <div className="result-label">Win Rate</div>
                  </div>
                  <div className="result-item">
                    <div className="result-value">4.3:1</div>
                    <div className="result-label">Avg. Risk/Reward</div>
                  </div>
                  <div className="result-item">
                    <div className="result-value">27</div>
                    <div className="result-label">Losing Trades</div>
                  </div>
                  <div className="result-item">
                    <div className="result-value">126</div>
                    <div className="result-label">Winning Trades</div>
                  </div>
                </div>
                <div className="result-conclusion">
                  <p>
                    <span className="highlight-text">Key Takeaway:</span> Consistency and asymmetric opportunities compound dramatically when backed by statistical edge
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="social-proof-bar">
            <div className="social-proof-item">
              <div className="proof-icon">⭐</div>
              <div className="proof-value">4.9/5</div>
              <div className="proof-label">Average Rating</div>
            </div>
            <div className="social-proof-item">
              <div className="proof-icon">👨‍💼</div>
              <div className="proof-value">2,784+</div>
              <div className="proof-label">Active Traders</div>
            </div>
            <div className="social-proof-item">
              <div className="proof-icon">📊</div>
              <div className="proof-value">$1.3B+</div>
              <div className="proof-label">Managed with Price on Rails</div>
            </div>
            <div className="social-proof-item">
              <div className="proof-icon">🏆</div>
              <div className="proof-value">83%</div>
              <div className="proof-label">Beat Market Returns</div>
            </div>
          </div>
          
          <div className="final-cta">
            <h3>It's Your Turn to Develop a Statistical Edge</h3>
            <p>Position yourself among the traders who understand that consistency comes from mathematical advantage, not luck</p>
            <a href="#pricing-tiers" className="btn-primary btn-large">
              Secure Your Statistical Edge
            </a>
            <div className="seat-counter">
              <span className="counter-label">Venture Partnership:</span>
              <span className="counter-value">{availableLifetimeSeats} seat remaining</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Address Objections */}
      <section className="faq-section">
        <div className="container">
          <div className="faq-header">
            <h2>Questions About Your Statistical Edge</h2>
            <p>Everything serious traders need to know about developing a mathematical advantage</p>
          </div>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is the pricing worth the investment?</h3>
              <p>If you're thinking about cost rather than ROI, you're still in the gambling mindset. Venture capitalists don't worry about the price of admission—they focus on asymmetric returns. Our average trader sees a 4.2x return on their investment within 12 months.</p>
            </div>

            <div className="faq-item">
              <h3>How does your win rate compare to competitors?</h3>
              <p>Most trading systems achieve 50-60% win rates—essentially a coin flip with extra steps. Our 78-83% win rate places us in territory typically reserved for institutional quant firms. We focus on asymmetric setups where retail traders have advantages that institutions don't.</p>
            </div>

            <div className="faq-item">
              <h3>What if I've tried systems before that didn't work?</h3>
              <p>Most systems fail because they're based on pattern recognition rather than statistical edge. We're offering a mathematical advantage based on AI analysis of asymmetric risk opportunities. Our edge persists because it's based on fundamental market inefficiencies.</p>
            </div>

            <div className="faq-item">
              <h3>Why is the Venture Partnership limited to {availableLifetimeSeats} seat?</h3>
              <p>Statistical edge exists because of market inefficiencies. If too many traders exploit the same edge, it weakens for everyone. Each Venture Partner receives dedicated server resources and personalized strategy consultation, requiring us to limit partnerships.</p>
            </div>
          </div>
          
          <div className="not-for-everyone">
            <h3>This Statistical Edge Isn't For Everyone</h3>
            <p>Price on Rails is designed for traders who understand that consistent profits come from mathematical advantage, not luck or "perfect" indicators. If you're ready to adopt the venture capital mindset of statistical edge and asymmetric returns, you've found your platform.</p>
            
            <div className="final-action-buttons">
              <a href="#pricing-tiers" className="btn-primary">
                Secure Your Edge Now
              </a>
              <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-outline">
                Schedule Strategy Call First
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Consultation Section */}
      <section className="contact-options meeting-focus">
        <div className="container">
          <div className="booking-header">
            <h2>Discuss Your <span className="highlight">Statistical Edge Strategy</span></h2>
            <p>Schedule a private consultation with our trading strategists to explore how Price on Rails can be calibrated to your specific capital requirements</p>
          </div>
          <div className="booking-card">
            <div className="booking-card-content">
              <div className="booking-icon">
                <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                </svg>
              </div>
              <h3>Strategic Edge Consultation</h3>
              <div className="booking-features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Custom statistical approach for your capital level</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Live AI edge demonstration with your watchlist</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>30-minute dedicated strategy session</span>
                </div>
              </div>
              <div className="booking-cta">
                <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                  Reserve Your Strategy Session
                </a>
                <p className="booking-note">No obligation. Pure strategic value.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default MentalistPricingPage; 